import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState({ tier: 'anonymous', used: 0, limit: 5, remaining: 5 });

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      await fetchUsage();
    } catch (error) {
      setUser(null);
      await fetchUsage();
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/usage`, {
        withCredentials: true
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    await fetchUsage();
    return response.data;
  };

  const signup = async (email, password, name) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/signup`,
      { email, password, name },
      { withCredentials: true }
    );
    setUser(response.data);
    await fetchUsage();
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    await fetchUsage();
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, usage, login, signup, logout, fetchUser, fetchUsage }}>
      {children}
    </AuthContext.Provider>
  );
};